import { object, string } from 'yup';
import { SchemaLike } from 'yup/lib/types';

import { numberOptionalValidation, numberRequiredValidation } from './number';
import { optionalStringValidation, requiredStringValidation } from './strings';

import { isCanada } from '../../constants/countries';
import { isValidProvinceAbbreviation } from '../../libs/provinces';

export const zipRegExp = /^(\d{5}(?:-\d{4})??)$/;
export const caPostalCodeRegExp = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;

// New estimates carry `country` on the address; credit app forms hold the deal country on the
// form root, which Formik passes to Yup as the context (`$country`).
export const ADDRESS_COUNTRY_KEYS = ['country', '$country'];
export const isCanadianAddress = (
  addressCountry?: string | null,
  dealCountry?: string | null,
): boolean => isCanada(addressCountry ?? dealCountry);

export const requirePreviousAddress = (yearsAtHome: number | undefined | null = null) =>
  yearsAtHome !== null && yearsAtHome < 2;
export const preAddressReq = (then: SchemaLike) =>
  object()
    .nullable()
    .when('address.years_at_home', {
      is: (years: number) => requirePreviousAddress(years),
      then,
    });

export const zipValidationOptional = (person?: string) =>
  string().when(ADDRESS_COUNTRY_KEYS, {
    is: isCanadianAddress,
    then: optionalStringValidation.matches(
      caPostalCodeRegExp,
      `Please enter a valid ${person || ''}postal code`,
    ),
    otherwise: optionalStringValidation.matches(
      zipRegExp,
      `Please enter a valid ${person || ''}zip code`,
    ),
  });

export const zipValidationRequired = (person?: string) =>
  string().when(ADDRESS_COUNTRY_KEYS, {
    is: isCanadianAddress,
    then: requiredStringValidation(`${person || ''}Postal Code`).matches(
      caPostalCodeRegExp,
      `Please enter a valid ${person || ''}postal code`,
    ),
    otherwise: requiredStringValidation(`${person || ''}Zip Code`).matches(
      zipRegExp,
      `Please enter a valid ${person || ''}zip code`,
    ),
  });
export const cityValidation = (person?: string) => requiredStringValidation(`${person || ''}City`);
export const countyValidation = (person?: string, isRequired = true) =>
  string().when(ADDRESS_COUNTRY_KEYS, {
    is: isCanadianAddress,
    then: optionalStringValidation,
    otherwise: isRequired
      ? requiredStringValidation(`${person || ''}County`)
      : optionalStringValidation,
  });

const usStateValidation = (person?: string, isRequired = true) =>
  (isRequired ? requiredStringValidation(`${person || ''}State`) : optionalStringValidation).test(
    'customer.address.state-select-state',
    `${person || ''}State is required`,
    (value) => {
      if (value === 'State') {
        return false;
      }
      return true;
    },
  );

const provinceValidation = (person?: string, isRequired = true) =>
  (isRequired
    ? requiredStringValidation(`${person || ''}Province`)
    : optionalStringValidation
  ).test(
    'customer.address.state-select-province',
    `Please select a valid ${person || ''}Province`,
    (value) => !value || isValidProvinceAbbreviation(value),
  );

export const stateValidation = (person?: string, isRequired = true) =>
  string().when(ADDRESS_COUNTRY_KEYS, {
    is: isCanadianAddress,
    then: provinceValidation(person, isRequired),
    otherwise: usStateValidation(person, isRequired),
  });

export const personAddressValidation = (person?: string, isRequired = true) => ({
  address_line: requiredStringValidation(`${person || ''}Address`),
  address_line_2: optionalStringValidation,
  zip: zipValidationRequired(person),
  city: cityValidation(person),
  state: stateValidation(person),
  county: countyValidation(person, isRequired),
});

const personAddressDetailsValidation = (person?: string, monthlyPayment?: boolean) => ({
  monthly_payment: monthlyPayment
    ? numberRequiredValidation(`${person || ''}Monthly Payment`)
    : numberOptionalValidation,
  years_at_home: numberRequiredValidation(`${person || ''}Years at home`),
  months_at_home: numberRequiredValidation(`${person || ''}Months at home`),
});

export const fullAddressValidation = (person?: string, monthlyPayment?: boolean) =>
  object().shape({
    ...personAddressValidation(person),
    ...personAddressDetailsValidation(person, monthlyPayment),
  });
export const previousAddressValidation = (person?: string) =>
  preAddressReq(fullAddressValidation(person).nullable());
